import { ITEM_TYPES } from '../../constants';
import { calculateMonthlyPayment } from './debt';

/**
 * Apply every timeline event that falls in `currentDate`'s month.
 *
 * Each event type settles against cash the way a real transaction would:
 *   add      — a loan pays out into cash; property costs its down payment;
 *              an investment costs its full value
 *   update   — a changed valuation moves the difference through cash, and a
 *              changed interest rate re-derives the payment
 *   delete   — the item is sold, proceeds net of any mortgage land in cash
 *   oneTime  — a bare cash movement
 *
 * Returns new state rather than mutating the caller's variables.
 */
export const applyTimelineEvents = ({
    items,
    liquidAssets,
    currentDate,
    timelineEvents,
    isScenarioActive,
}) => {
    if (!isScenarioActive || !timelineEvents || timelineEvents.length === 0) {
        return { items, liquidAssets };
    }

    const activeTimelineEvents = timelineEvents.filter(event => !event.isDisabled);

    const processTimelineEvent = (event, currentSimulatedLiquidAssets, simulatedPortfolioItems) => {
        const eventDate = new Date(event.eventDate);
        if (eventDate.getFullYear() === currentDate.getFullYear() && eventDate.getMonth() === currentDate.getMonth()) {
            switch (event.eventType) {
                case 'add': {
                    const newItem = { ...event.itemData, id: `sim-${new Date().getTime()}-${Math.random()}`, isActive: true, simulatedLoanBalance: 0, simulatedMortgageBalance: 0 };

                    if (newItem.type === ITEM_TYPES.LOAN) {
                        newItem.simulatedLoanBalance = parseFloat(newItem.loanAmount || 0);
                        newItem.simulatedLoanTermRemaining = parseInt(newItem.loanTermTotalMonths || 0);
                        currentSimulatedLiquidAssets += newItem.simulatedLoanBalance;
                    }
                    if (newItem.type === ITEM_TYPES.REAL_ESTATE) {
                        if (newItem.hasMortgage) {
                            newItem.simulatedMortgageBalance = parseFloat(newItem.mortgageInitialAmount || 0);
                            newItem.simulatedMortgageTermRemaining = parseInt(newItem.mortgageTermTotalMonths || 0);
                        }
                        const downPayment = parseFloat(newItem.currentValue || 0) - (newItem.hasMortgage ? newItem.simulatedMortgageBalance : 0);
                        currentSimulatedLiquidAssets -= downPayment;
                    }
                    if (newItem.type === ITEM_TYPES.OTHER_INVESTMENT) {
                        currentSimulatedLiquidAssets -= parseFloat(newItem.currentValue || 0);
                    }
                    simulatedPortfolioItems.push(newItem);
                    break;
                }
                case 'update': {
                    const itemToUpdate = simulatedPortfolioItems.find(item => item.id === event.targetItemId);
                    if (itemToUpdate && Object.prototype.hasOwnProperty.call(event.itemDataUpdate, 'currentValue')) {
                        const oldValue = parseFloat(itemToUpdate.currentValue || 0);
                        const newValue = parseFloat(event.itemDataUpdate.currentValue || 0);
                        const valueDifference = oldValue - newValue;
                        currentSimulatedLiquidAssets += valueDifference;
                    }

                    simulatedPortfolioItems = simulatedPortfolioItems.map(item => {
                        if (item.id === event.targetItemId) {
                            const updatedItem = { ...item, ...event.itemDataUpdate };
                            if (updatedItem.type === ITEM_TYPES.LOAN && event.itemDataUpdate.interestRate) {
                                updatedItem.monthlyPayment = calculateMonthlyPayment(updatedItem.loanAmount, updatedItem.interestRate, updatedItem.loanTermTotalMonths);
                            }
                            if (updatedItem.type === ITEM_TYPES.REAL_ESTATE && updatedItem.hasMortgage && event.itemDataUpdate.mortgageInterestRate) {
                                updatedItem.mortgageMonthlyPayment = calculateMonthlyPayment(updatedItem.mortgageInitialAmount, updatedItem.mortgageInterestRate, updatedItem.mortgageTermTotalMonths);
                            }
                            return updatedItem;
                        }
                        return item;
                    });
                    break;
                }
                case 'delete': {
                    const itemToDelete = simulatedPortfolioItems.find(item => item.id === event.targetItemId);
                    if (itemToDelete) {
                        let proceeds = 0;
                        if (itemToDelete.type === ITEM_TYPES.REAL_ESTATE || itemToDelete.type === ITEM_TYPES.OTHER_INVESTMENT) {
                            proceeds = parseFloat(itemToDelete.currentValue || 0);
                            if (itemToDelete.type === ITEM_TYPES.REAL_ESTATE && itemToDelete.hasMortgage) {
                                proceeds -= parseFloat(itemToDelete.simulatedMortgageBalance || 0);
                            }
                        }
                        currentSimulatedLiquidAssets += proceeds;
                    }
                    simulatedPortfolioItems = simulatedPortfolioItems.filter(item => item.id !== event.targetItemId);
                    break;
                }
                case 'oneTime': {
                    const amount = parseFloat(event.amount || 0);
                    if (!isNaN(amount)) {
                        currentSimulatedLiquidAssets += amount;
                    }
                    break;
                }
                default:
                    break;
            }
        }
        return { currentSimulatedLiquidAssets, simulatedPortfolioItems };
    };

    let tempLiquidAssets = liquidAssets;
    let tempPortfolioItems = [...items];

    activeTimelineEvents.forEach(event => {
        const result = processTimelineEvent(event, tempLiquidAssets, tempPortfolioItems);
        tempLiquidAssets = result.currentSimulatedLiquidAssets;
        tempPortfolioItems = result.simulatedPortfolioItems;
    });

    return { items: tempPortfolioItems, liquidAssets: tempLiquidAssets };
};
